import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Paper } from '@mui/material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useState, useCallback, useMemo, useEffect } from 'react';
import SearchField from 'src/shared/components/form/searchField';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { AddCircle } from '@mui/icons-material';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import InvoiceDataGrid from '../components/invoiceDataGrid';
import InvoiceTabs, { invoiceTabs } from '../components/invoiceTabs';
import { InvoiceGroupsAndMissions } from '../components/invoiceGroupsAndMissions';
import { InvoiceSearchFilter } from '../models/invoiceSearchFilter';
import { CreateInvoiceModal } from '../components/createInvoiceModal';
import { CreateMemberInvoiceStepperModal } from '../components/createMemberInvoiceStepperModal';

export const InvoiceOverviewPage = () => {
  const { t } = useTranslation(['invoice']);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const { currentUser } = useGlobals();
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState<boolean>(false);
  const [showCreateMemberInvoiceModal, setShowCreateMemberInvoiceModal] = useState<boolean>(false);
  // const [searchFilterInitialized, setSearchFilterInitialized] = useState<boolean>(false); // Om avancerat sök ska in
  const [invoiceSearchFilter, setInvoiceSearchFilter] = useLocalStorage(LocalStorageKeys.InvoiceSearchFilter, {
    status: invoiceTabs.filter((x) => x.key === 'UnPaid')[0],
  } as unknown as InvoiceSearchFilter);

  const sysProducts = getSysProducts(currentUser);
  const hasMemberReg = sysProducts.some((x) => x.yvName.toLowerCase() === SysProductYvNameEnum.MemberReg.toLowerCase());

  const searchFilter: InvoiceSearchFilter = useMemo(() => {
    return {
      ownerId: invoiceSearchFilter?.ownerId,
      invoiceMissionId: invoiceSearchFilter?.invoiceMissionId,
      searchString: invoiceSearchFilter?.searchString,
      status: invoiceSearchFilter?.status,
      pageIndex: invoiceSearchFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
      pageSize: invoiceSearchFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
    } as unknown as InvoiceSearchFilter;
  }, [
    invoiceSearchFilter?.invoiceMissionId,
    invoiceSearchFilter?.ownerId,
    invoiceSearchFilter?.pageIndex,
    invoiceSearchFilter?.pageSize,
    invoiceSearchFilter?.searchString,
    invoiceSearchFilter?.status,
  ]);

  const loadingCallback = useCallback(
    (isLoading: boolean) => {
      if (isMounted()) {
        setLoading(isLoading);
      }
    },
    [isMounted],
  );

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setInvoiceSearchFilter, tempSearchString]);

  return (
    <>
      <GridWrapper>
        <Grid item xs={12} md={4}>
          <MainContentHeader title={t('invoice:Invoices')} />
        </Grid>
        <Grid item xs={12} md={8} style={{ display: 'flex', justifyContent: 'flex-end', gridColumnGap: '5px' }}>
          {hasMemberReg && (
            <Button
              onClick={() => setShowCreateMemberInvoiceModal(true)}
              variant='contained'
              color='secondary'
              startIcon={<AddCircle fontSize='small' />}
              disableElevation
            >
              {t('invoice:createMemberInvoice.createMemberInvoiceButton')}
            </Button>
          )}
          <Button onClick={() => setShowCreateInvoiceModal(true)} variant='contained' color='secondary' startIcon={<AddCircle />} disableElevation>
            {t('invoice:createInvoice.createInvoiceButton')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <PageFilterSection
              leftContent={
                <SearchField
                  initialValue={searchFilter.searchString || ''}
                  onSearch={(newSearchString) => {
                    setSearchInitialized(true);
                    setTempSearchString(newSearchString);
                  }}
                />
              }
              rightContent={
                <Box sx={{ display: 'flex' }}>
                  <InvoiceGroupsAndMissions
                    callback={(ownId, missionId) => {
                      setInvoiceSearchFilter((prev) => ({ ...prev, ...{ invoiceMissionId: missionId, ownerId: ownId } }));
                    }}
                    initialInvoiceGroupId={searchFilter.ownerId}
                    initialInvoiceMissionId={searchFilter.invoiceMissionId}
                  />
                </Box>
              }
            />
            <InvoiceTabs
              initialValue={searchFilter.status || undefined}
              disableAll={loading}
              callback={(val) => setInvoiceSearchFilter((prev) => ({ ...prev, ...{ status: val, pageIndex: 0 } }))}
            />
            <Divider />
            <InvoiceDataGrid
              loadingCallback={loadingCallback}
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) => {
                setInvoiceSearchFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }));
              }}
            />
          </Paper>
        </Grid>
      </GridWrapper>
      <CreateMemberInvoiceStepperModal modalOpen={showCreateMemberInvoiceModal} onClose={() => setShowCreateMemberInvoiceModal(false)} />
      <CreateInvoiceModal
        modalOpen={showCreateInvoiceModal}
        onClose={() => setShowCreateInvoiceModal(false)}
        onSave={() => setShowCreateInvoiceModal(false)}
      />
    </>
  );
};
